import { Info, Play, TriangleAlert } from "lucide-react"
import * as React from "react"
import { Link } from "react-router-dom"

import { statusTone } from "@/components/rules/condition-tree"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Segmented } from "@/components/ui/segmented"
import {
  MISSING_GROUPS,
  replayRecord,
  type MissingField,
  type MissingGroup,
  type MissingReason,
  type ReplayMode,
  type ReplayResult,
  type ReplayRow,
  type RuleOutcome,
} from "@/lib/api"
import { EMPTY, formatInt, formatText } from "@/lib/format"
import { cn } from "@/lib/utils"

/**
 * Страница реплея — прогон одной записи по правилам из базы.
 *
 * Считает **живая** ручка `POST /api/scoring/replay`: правила, условия,
 * тумблеры ON/OFF и пороги берутся из той же базы, что показывают вкладки
 * правил. Страница ничего не досчитывает за движок — ни итогового балла, ни
 * решения, ни признака полноты; всё это печатается ровно таким, каким пришло.
 *
 * Отсюда и главное правило экрана: `score: null` — это прочерк, а не ноль.
 * Ноль означал бы «посчитали, ничего не набралось», а `null` — «собрать итог
 * нечем, часть правил проверить не удалось».
 *
 * Боевая выгрузка сюда не грузится: 44 тысячи строк ПДн гоняет локальный
 * `scripts/replay_csv.py` (SPEC, AMEND-1 §BLOCK 3).
 */

/**
 * Пример записи для поля ввода.
 *
 * Выдуманный целиком: `example.com` и `isp` — не данные клиента и не строка из
 * выгрузки. Имена колонок настоящие (`reference/FIELD-MAP.md`), потому что по
 * ним движок и ищет значения; выдумать их было бы вредно — запись не сработала
 * бы ни по одному условию, и экран показал бы пустоту вместо разбора.
 */
const EXAMPLE_RECORD = `{
  "email_raw_email": "test+promo@example.com",
  "ip_type": "isp"
}`

/** Порядок статусов на плашках — сначала то, что посчиталось. */
const STATUS_ORDER = [
  "matched",
  "not_matched",
  "skipped_no_condition",
  "skipped_no_data",
  "skipped_needs_history",
  "not_reproducible",
]

/**
 * Что каждый статус значит по-человечески — **наша** подпись, не слово движка.
 *
 * Движок объясняет себя сам ровно в четырёх случаях из шести: у `matched` и
 * `not_matched` поле `reason` приходит пустым (замер живой ручки 2026-09-16:
 * 2 и 3 правила соответственно, `reason: null` у всех пяти). Пустая клетка
 * «Why» читается как «почему — неизвестно», хотя известно совершенно точно:
 * условие сверили с записью, и оно сошлось или не сошлось.
 *
 * Поэтому подпись подставляется только там, где движку сказать нечего, и
 * набрана приглушённо — рядом с настоящим `reason` её нельзя принять за
 * разбор конкретного правила.
 */
const STATUS_MEANING: Record<string, string> = {
  matched: "the condition was checked against the record and held — the score above was added",
  not_matched: "the condition was checked against the record and did not hold — nothing was added",
  skipped_no_condition: "no condition has been written for this rule yet, so there was nothing to check",
  skipped_no_data: "the record has no value in the columns this condition reads",
  skipped_needs_history: "this rule counts events over a time window; one record is not enough",
  not_reproducible: "SEON decides this one inside its own service — we cannot recompute it",
}

/** Сколько строк правил рисуем разом. Ограничение названо на экране вслух. */
const RULES_SHOWN = 200

/**
 * Источник поля (`field_sources`, AMEND-39 шаг 3) — подпись и порядок показа.
 * Неизвестное имя (новый офлайн-источник) печатается как пришло, в конце.
 */
const SOURCE_LABEL: Record<string, string> = {
  record: "Record",
  proxycheck: "proxycheck",
  refdata: "BIN directory",
  hibp: "HIBP",
  libphonenumber: "libphonenumber",
  local_phone_lists: "Disposable-number lists",
}

/**
 * Что за источник — строкой под подписью. Офлайн-парсер номера (AMEND-40)
 * не ходит в сеть: оператора и VoIP он не знает. Одноразовость (AMEND-49) —
 * из открытых списков Castle и iP1SMS, которые сервис держит у себя.
 */
const SOURCE_NOTE: Record<string, string> = {
  libphonenumber: "offline parser",
  local_phone_lists: "Castle + iP1SMS, kept locally",
}

/**
 * Группы «не хватает данных». Ручка шлёт русскую подпись в `label`, экран
 * весь английский — подпись своя, по ключу группы; смысл тот же, что в
 * `backend/field_provenance.py`, и группу придумывает не фронт.
 */
const MISSING_LABEL: Record<MissingGroup, string> = {
  request: "Must arrive in the payment provider’s request",
  device_sdk: "Device data — only from the SDK",
  no_source: "We have no source for it",
  source_failed: "Source did not answer / quota / not in cache",
}

/** Короткая подпись группы для клетки «Why» таблицы правил. */
const MISSING_SHORT: Record<MissingGroup, string> = {
  request: "must come in the request",
  device_sdk: "device SDK only",
  no_source: "no source",
  source_failed: "source did not answer",
}

const MISSING_TONE: Record<MissingGroup, "neutral" | "review" | "accent"> = {
  request: "accent",
  device_sdk: "neutral",
  no_source: "neutral",
  source_failed: "review",
}

function sourceLabel(source: string) {
  return SOURCE_LABEL[source] ?? source
}

export function ReplayTab() {
  const [text, setText] = React.useState(EXAMPLE_RECORD)
  const [mode, setMode] = React.useState<ReplayMode>("full")
  const [referenceField, setReferenceField] = React.useState("")
  const [result, setResult] = React.useState<ReplayResult | null>(null)
  const [error, setError] = React.useState<string | null>(null)
  const [running, setRunning] = React.useState(false)
  // Колонки **отправленной** записи, а не того, что сейчас в поле. Плашка
  // «принято N из M» считает по ним: возьми мы длину текущего текста, дописанная
  // после прогона строка молча сдвинула бы знаменатель у чужого результата.
  const [sentColumns, setSentColumns] = React.useState<string[]>([])

  // Режим custom_only без колонки эталона ручка отвергает (422): вклад
  // дефолтных правил ей неоткуда взять, а считать их нулём она отказывается.
  // Гасим кнопку заранее — запрос, который заведомо вернёт отказ, посылать
  // незачем, а причина у поля уже написана словами.
  const needsReference = mode === "custom_only" && referenceField.trim() === ""

  async function run() {
    let record: unknown
    try {
      record = JSON.parse(text)
    } catch (cause) {
      setError(`The record is not valid JSON — ${(cause as Error).message}`)
      setResult(null)
      return
    }
    if (record === null || typeof record !== "object" || Array.isArray(record)) {
      setError("The record must be a JSON object of columns, e.g. {\"ip_type\": \"isp\"}")
      setResult(null)
      return
    }

    const columns = Object.keys(record as Record<string, unknown>)

    setRunning(true)
    try {
      const response = await replayRecord(record as Record<string, unknown>, {
        mode,
        reference_score_field: referenceField.trim() || undefined,
      })
      setResult(response)
      setSentColumns(columns)
      setError(null)
    } catch (cause) {
      // Отчёт прошлого прогона убираем: оставленный рядом с ошибкой, он
      // читался бы как результат текущей записи.
      setResult(null)
      setSentColumns([])
      setError(cause instanceof Error ? cause.message : "Request failed")
    } finally {
      setRunning(false)
    }
  }

  return (
    <div className="grid gap-4 p-5 lg:grid-cols-[380px_minmax(0,1fr)]">
      <section className="animate-panel-in h-fit rounded border border-line bg-surface shadow-panel">
        <header className="border-b border-line px-4 py-3">
          <h2 className="card-title">Record</h2>
          <p className="mt-1 text-xs leading-relaxed text-fg-subtle">
            Columns as they are named in the SEON export. The example below is made up — replace it
            with the record you want to check.
          </p>
        </header>

        <div className="space-y-3 px-4 py-3">
          <textarea
            value={text}
            onChange={(event) => setText(event.target.value)}
            spellCheck={false}
            rows={12}
            aria-label="Record JSON"
            className="w-full resize-y rounded border border-line-strong bg-surface-sunken px-2.5 py-2 font-mono text-xs leading-relaxed text-fg outline-none transition-colors duration-150 ease-snap focus:border-accent"
          />

          <div>
            <span className="stencil-label mb-1 block text-fg-subtle">Rules</span>
            <Segmented
              label="Rules included"
              value={mode}
              onChange={setMode}
              options={[
                { value: "full", label: "All rules" },
                { value: "custom_only", label: "Custom only" },
              ]}
            />
          </div>

          <div>
            <span className="stencil-label mb-1 block text-fg-subtle">
              Reference score column
            </span>
            <Input
              value={referenceField}
              onChange={(event) => setReferenceField(event.target.value)}
              placeholder={mode === "custom_only" ? "required in this mode" : "optional"}
              spellCheck={false}
            />
            <p className="mt-1 text-xs leading-relaxed text-fg-subtle">
              {mode === "custom_only"
                ? "Custom-only scoring needs the column that already holds the default rules’ contribution. Without it the engine refuses rather than counting the default rules as zero."
                : "Name the export column with SEON’s own score to have it shown next to ours. We do not guess the column name."}
            </p>
          </div>

          <Button
            variant="primary"
            onClick={run}
            disabled={running || needsReference}
            title={needsReference ? "Name the reference score column first" : undefined}
            className="w-full"
          >
            <Play className="h-3.5 w-3.5" strokeWidth={2.5} />
            {running ? "Running…" : "Run replay"}
          </Button>
          {needsReference && (
            <p className="text-xs leading-relaxed text-review">
              Name the reference score column above, or switch back to all rules.
            </p>
          )}
        </div>
      </section>

      <div className="min-w-0 space-y-4">
        {error && (
          <section className="animate-panel-in rounded border border-decline/35 bg-decline/5 p-4">
            <p className="flex items-start gap-2 text-xs leading-relaxed text-decline">
              <TriangleAlert className="mt-px h-4 w-4 shrink-0" strokeWidth={2} />
              <span>{error}</span>
            </p>
          </section>
        )}

        {!result && !error && (
          <section className="rounded border border-dashed border-line-strong bg-surface p-10 text-center">
            <p className="text-sm text-fg-muted">Nothing has been run yet.</p>
            <p className="mx-auto mt-1 max-w-sm text-xs text-fg-subtle">
              The page shows the engine’s own answer for one record — no numbers appear here until
              a run returns them.
            </p>
          </section>
        )}

        {result && <ReplayReport result={result} sentColumns={sentColumns} />}
      </div>
    </div>
  )
}

function ReplayReport({
  result,
  sentColumns,
}: {
  result: ReplayResult
  sentColumns: string[]
}) {
  const row = result.rows[0]
  if (!row) {
    return (
      <section className="rounded border border-line bg-surface p-6 text-center shadow-panel">
        <p className="text-sm text-fg-muted">The engine returned no rows for this record.</p>
      </section>
    )
  }

  return (
    <>
      <Columns unknown={row.unknown_fields} sent={sentColumns} />
      <Verdict result={result} row={row} />
      <Counts row={row} />
      <Sources row={row} />
      <MissingData fields={row.missing_data} />
      <RulesTable rules={row.rules} />
    </>
  )
}

/**
 * Плашка «принято N из M» — первое, что видно над результатом.
 *
 * Список неузнанных колонок стоял внизу карточки вердикта и читался как
 * примечание, хотя это единственное место, где видна опечатка в имени: ручка
 * на `sesion_os` не ругается, она просто не находит колонку, и правило уходит
 * в `skipped_no_data` наравне с теми, у кого данных правда нет.
 *
 * Число принятых даёт то, чего не давал голый список: «принято 25 из 26» —
 * промах в одном имени, «принято 0 из 26» — запись целиком мимо (не тот файл
 * выгрузки, не те имена). Знаменатель — колонки **отправленной** записи, а не
 * `known_fields()`: сверять присланное со справочником из шести сотен имён
 * читателю незачем.
 *
 * Чистая запись плашки не показывает вовсе: «принято 8 из 8» сообщать нечего,
 * а рамка, появляющаяся каждый прогон, перестаёт значить что-либо к третьему.
 */
function Columns({ unknown, sent }: { unknown: string[]; sent: string[] }) {
  if (unknown.length === 0) return null
  const accepted = sent.length - unknown.length

  return (
    <section className="animate-panel-in rounded border border-review/40 bg-review/5 shadow-panel">
      <div className="flex items-start gap-2.5 px-4 py-3">
        <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0 text-review" strokeWidth={2} />
        <div className="min-w-0">
          <p className="text-xs font-medium leading-relaxed text-review">
            {accepted >= 0
              ? `Accepted ${formatInt(accepted)} of ${formatInt(sent.length)} columns`
              : "Some columns were not accepted"}
            {" — no rule reads "}
            {unknown.length === 1 ? "this name:" : `these ${formatInt(unknown.length)} names:`}
          </p>
          <div className="mt-2 flex flex-wrap gap-1">
            {unknown.map((field) => (
              <span
                key={field}
                className="rounded-sm border border-review/30 bg-surface px-1.5 py-0.5 font-mono text-xs text-fg"
              >
                {field}
              </span>
            ))}
          </div>
          <p className="mt-2 text-xs leading-relaxed text-fg-muted">
            Names are printed exactly as they were sent. An extra column is harmless by itself — a
            misspelt one is not: the rule that wanted it reads nothing and ends up under{" "}
            <span className="font-mono">skipped_no_data</span>.
          </p>
        </div>
      </div>
    </section>
  )
}

function Verdict({ result, row }: { result: ReplayResult; row: ReplayRow }) {
  const decisionTone =
    row.decision === "approve" ? "approve" : row.decision === "decline" ? "decline" : "review"

  return (
    <section className="animate-panel-in rounded border border-line bg-surface shadow-panel">
      <div className="flex flex-wrap items-end gap-x-10 gap-y-4 px-4 py-4">
        <div>
          <span className="stencil-label block text-fg-subtle">Score</span>
          {row.score === null ? (
            <span className="text-3xl font-medium text-fg-subtle">{EMPTY}</span>
          ) : (
            <span className="text-3xl font-medium text-fg">{row.score}</span>
          )}
        </div>
        <div>
          <span className="stencil-label block text-fg-subtle">Decision</span>
          {row.decision === null ? (
            <span className="text-sm text-fg-subtle">{EMPTY}</span>
          ) : (
            <Badge tone={decisionTone} className="text-xs">
              {row.decision}
            </Badge>
          )}
        </div>
        {/*
         * «Complete» и «Rules checked» стояли двумя клетками: первая говорила
         * `no`, вторая — `549 / 649`. Само по себе `no` не значит ничего: оно
         * не называет ни что не проверено, ни сколько. Клетка теперь одна, и
         * флаг читается тем же числом, которым он и получен, — `checked_rules`
         * это `matched + not_matched`, `rules_total` — все правила прогона.
         */}
        <div>
          <span className="stencil-label block text-fg-subtle">Complete</span>
          {row.complete ? (
            <Badge tone="approve" className="text-xs">
              all {formatInt(result.rules_total)} rules checked
            </Badge>
          ) : (
            <Badge tone="review" className="text-xs">
              checked {formatInt(row.checked_rules)} of {formatInt(result.rules_total)}
            </Badge>
          )}
        </div>
        <div>
          <span className="stencil-label block text-fg-subtle">Conditions</span>
          <span className="text-xs text-fg-muted">
            {formatText(result.conditions_version)}
          </span>
        </div>
      </div>

      {!row.complete && (
        <p className="flex items-start gap-2 border-t border-line bg-review/5 px-4 py-2.5 text-xs leading-relaxed text-review">
          <TriangleAlert className="mt-px h-3.5 w-3.5 shrink-0" strokeWidth={2} />
          <span>
            {formatInt(row.skipped_rules)} of {formatInt(result.rules_total)} rules were not
            checked, so there is no total to give. The sum of the rules that did run is{" "}
            <span>{row.computed_score}</span> — that is a partial figure, not
            this record’s score.
          </span>
        </p>
      )}

      {result.thresholds.is_assumption && (
        <p className="flex items-start gap-2 border-t border-line bg-review/5 px-4 py-2.5 text-xs leading-relaxed text-review">
          <TriangleAlert className="mt-px h-3.5 w-3.5 shrink-0" strokeWidth={2} />
          <span>
            Thresholds are an assumption, not SEON’s: approve below{" "}
            <span>{result.thresholds.approve_below}</span>, decline at{" "}
            <span>{result.thresholds.decline_at}</span>. Any decision above is
            only as right as they are.
          </span>
        </p>
      )}

      <Reference row={row} />

      {/*
       * Предупреждения движка идут дословно и под своей подписью. Полосы выше
       * их пересказывают — не дублирование по недосмотру: там наша формулировка
       * того, что цифра значит, здесь слово самого движка, которое правим не мы.
       */}
      {row.warnings.length > 0 && (
        <div className="border-t border-line px-4 py-2">
          <span className="stencil-label text-fg-subtle">From the engine</span>
          {row.warnings.map((warning) => (
            <p
              key={warning}
              className="mt-1 flex items-start gap-2 text-xs leading-relaxed text-fg-muted"
            >
              <Info className="mt-px h-3.5 w-3.5 shrink-0 text-fg-subtle" strokeWidth={2} />
              <span>{warning}</span>
            </p>
          ))}
        </div>
      )}

    </section>
  )
}

/** Эталонный скор SEON — показываем только то, что вернула ручка. */
function Reference({ row }: { row: ReplayRow }) {
  if (row.reference.status === "not_requested") return null
  return (
    <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 border-t border-line px-4 py-2.5 text-xs text-fg-muted">
      <span className="stencil-label text-fg-subtle">SEON reference</span>
      <span className="text-fg">{row.reference.score ?? EMPTY}</span>
      <span className="text-fg-subtle">{row.reference.status}</span>
      {row.reference.field && <span className="text-fg-subtle">{row.reference.field}</span>}
      {row.reference.note && <span className="leading-relaxed">{row.reference.note}</span>}
    </div>
  )
}

/**
 * Разбивка по статусам — счётчики самого движка (`counts`), не пересчёт по
 * показанным строкам. Пересчёт разошёлся бы с движком в тот же день, когда
 * список правил в ответе станет усечённым.
 */
function Counts({ row }: { row: ReplayRow }) {
  const known = STATUS_ORDER.filter((status) => status in row.counts)
  const extra = Object.keys(row.counts).filter((status) => !STATUS_ORDER.includes(status))

  return (
    <section className="animate-panel-in flex flex-wrap gap-2 rounded border border-line bg-surface px-4 py-3 shadow-panel">
      {[...known, ...extra].map((status) => (
        <div
          key={status}
          className="flex items-baseline gap-2 rounded-sm border border-line bg-surface-sunken px-2 py-1"
        >
          <span
            className={cn(
              "text-xs font-semibold uppercase tracking-[0.04em]",
              statusTone(status) === "decline" && "text-decline",
              statusTone(status) === "review" && "text-review",
              statusTone(status) === "neutral" && "text-fg-muted",
            )}
          >
            {status}
          </span>
          <span className="text-xs text-fg">{formatInt(row.counts[status])}</span>
        </div>
      ))}
    </section>
  )
}

/**
 * Откуда взялось каждое заполненное поле: сама запись, `enricher`
 * (proxycheck, справочник BIN, HIBP), офлайн-парсер номера
 * (libphonenumber) или списки одноразовых номеров. Поля сгруппированы по источнику —
 * на телефоне это несколько строк чипов, а не таблица в скроллере.
 *
 * Под чипами — статус каждого источника этого прогона словами ручки
 * (`enrichment.missing`): «нет в кэше», «enricher не ответил». Без него пустой
 * список у proxycheck не отличить от «ничего не дал».
 */
function Sources({ row }: { row: ReplayRow }) {
  const bySource = new Map<string, string[]>()
  for (const [field, source] of Object.entries(row.field_sources)) {
    bySource.set(source, [...(bySource.get(source) ?? []), field])
  }
  const order = Object.keys(SOURCE_LABEL)
  const rank = (source: string) => {
    const place = order.indexOf(source)
    return place === -1 ? order.length : place
  }
  const sources = [...bySource.keys()].sort((a, b) => rank(a) - rank(b))
  const notes = Object.entries(row.enrichment.missing)

  return (
    <section className="animate-panel-in rounded border border-line bg-surface shadow-panel">
      <header className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1 border-b border-line px-4 py-3">
        <h3 className="section-title">Where the data came from</h3>
        <span className="text-xs text-fg-subtle">
          {row.enrichment.cache_only ? "batch — cache only, no new calls" : "single record — like a live event"}
        </span>
      </header>
      {sources.length === 0 ? (
        <p className="px-4 py-3 text-xs text-fg-muted">No field that a rule reads is filled.</p>
      ) : (
        <dl className="divide-y divide-line/60">
          {sources.map((source) => (
            <div key={source} className="flex flex-col gap-1.5 px-4 py-2.5 sm:flex-row sm:gap-4">
              <dt className="flex shrink-0 flex-col items-start gap-0.5 sm:w-32">
                <Badge tone={source === "record" ? "neutral" : "approve"}>{sourceLabel(source)}</Badge>
                {SOURCE_NOTE[source] && (
                  <span className="text-xs text-fg-subtle">{SOURCE_NOTE[source]}</span>
                )}
              </dt>
              {/* Строка тянется по высоте dt (бейдж + пометка источника) — без
                  content-start/items-start чипы растягивались на всю эту высоту. */}
              <dd className="flex min-w-0 flex-wrap content-start items-start gap-1">
                {(bySource.get(source) ?? []).map((field) => (
                  <span
                    key={field}
                    className="max-w-full break-all rounded-sm border border-line bg-surface-sunken px-1.5 py-0.5 font-mono text-xs text-fg"
                  >
                    {field}
                  </span>
                ))}
              </dd>
            </div>
          ))}
        </dl>
      )}
      {/* `error` отдельно не печатаем: при сбое вызова каждый источник уже
          несёт «не обогащено: enricher не ответил» в `missing`. */}
      {notes.length > 0 && (
        <div className="border-t border-line px-4 py-2 text-xs leading-relaxed text-fg-muted">
          {notes.map(([source, note]) => (
            <p key={source} className="flex items-start gap-2">
              <Info className="mt-px h-3.5 w-3.5 shrink-0 text-fg-subtle" strokeWidth={2} />
              <span>
                <span className="text-fg">{sourceLabel(source)}</span>: {note}
              </span>
            </p>
          ))}
        </div>
      )}
    </section>
  )
}

/**
 * «Не хватает данных» — по группам, а не голым списком колонок. Группа и
 * пояснение (`note`) приходят из ручки; число — сколько правил записи не
 * досчитано из-за этого поля. Пустую панель не рисуем: всё досчитано.
 */
function MissingData({ fields }: { fields: MissingField[] }) {
  if (fields.length === 0) return null
  const groups = MISSING_GROUPS.map((group) => ({
    group,
    items: fields.filter((item) => item.group === group),
  })).filter(({ items }) => items.length > 0)

  return (
    <section className="animate-panel-in rounded border border-line bg-surface shadow-panel">
      <header className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1 border-b border-line px-4 py-3">
        <h3 className="section-title">Missing data</h3>
        <span className="text-xs text-fg-subtle">
          {formatInt(fields.length)} {fields.length === 1 ? "field" : "fields"} kept rules from being checked
        </span>
      </header>
      <div className="divide-y divide-line/60">
        {groups.map(({ group, items }) => (
          <div key={group} className="px-4 py-3">
            <div className="flex flex-wrap items-center gap-2">
              <Badge tone={MISSING_TONE[group]}>{MISSING_LABEL[group]}</Badge>
              <span className="text-xs text-fg-subtle">{formatInt(items.length)}</span>
            </div>
            <ul className="mt-2 space-y-1">
              {items.map((item) => (
                <li
                  key={item.field}
                  className="grid grid-cols-[minmax(0,1fr)_auto] gap-x-3 text-xs leading-relaxed sm:grid-cols-[minmax(0,14rem)_minmax(0,1fr)_auto]"
                >
                  <span className="break-all text-fg">{item.field}</span>
                  <span className="col-span-2 row-start-2 text-fg-muted sm:col-span-1 sm:row-start-auto">
                    {item.source && <span>{sourceLabel(item.source)}: </span>}
                    {item.note}
                  </span>
                  <span className="col-start-2 row-start-1 whitespace-nowrap text-right text-fg-subtle sm:col-start-auto sm:row-start-auto">
                    {formatInt(item.rules)} {item.rules === 1 ? "rule" : "rules"}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </section>
  )
}

/** Причина по каждому пустому полю правила — коротко, пояснение в подсказке. */
function MissingReasons({ reasons }: { reasons: MissingReason[] }) {
  return (
    <span className="mt-0.5 block space-y-0.5">
      {reasons.map((reason) => (
        <span key={reason.field} className="block" title={reason.note}>
          <span className="text-fg-subtle">{reason.field}</span>
          <span className="text-fg-subtle"> — {MISSING_SHORT[reason.group]}</span>
        </span>
      ))}
    </span>
  )
}

function RulesTable({ rules }: { rules: RuleOutcome[] }) {
  const [filter, setFilter] = React.useState<string>("all")

  const statuses = React.useMemo(() => {
    const seen = new Set(rules.map((rule) => rule.status))
    return ["all", ...STATUS_ORDER.filter((status) => seen.has(status))]
  }, [rules])

  const shown = filter === "all" ? rules : rules.filter((rule) => rule.status === filter)
  const visible = shown.slice(0, RULES_SHOWN)

  return (
    <section className="animate-panel-in rounded border border-line bg-surface shadow-panel">
      <header className="flex flex-wrap items-center justify-between gap-3 border-b border-line px-4 py-3">
        <h3 className="section-title">Rules</h3>
        {/* Фильтр — шесть статусов в ряд, на узком экране в строку не влезают.
            Скроллер на обёртке: сама группа остаётся цельной кнопочной лентой. */}
        <div className="min-w-0 max-w-full overflow-x-auto">
          <Segmented
            label="Filter rules by status"
            value={filter}
            onChange={setFilter}
            options={statuses.map((status) => ({ value: status, label: status }))}
          />
        </div>
      </header>

      <div className="overflow-x-auto">
        {/*
         * `table-fixed` здесь обязателен. При автоматической раскладке
         * `max-w-*` на `<td>` браузер считает пожеланием: колонка «Why» с
         * длинной причиной движка растягивала таблицу шире панели (+120px на
         * 1440) и рвала сетку. Ширины заданы явно, `min-w` держит читаемость —
         * на узком экране таблица едет внутри своего скроллера, как и на
         * вкладках правил.
         */}
        <table className="w-full min-w-[680px] table-fixed border-collapse text-sm">
          <thead>
            <tr className="border-b border-line text-left">
              <th className="stencil-label w-[30%] px-4 py-2 font-medium text-fg-subtle">Rule</th>
              <th className="stencil-label w-[22%] px-3 py-2 font-medium text-fg-subtle">Status</th>
              <th className="stencil-label w-[9%] px-3 py-2 text-right font-medium text-fg-subtle">
                Rule score
              </th>
              <th className="stencil-label w-[9%] px-3 py-2 text-right font-medium text-fg-subtle">
                Added
              </th>
              <th className="stencil-label px-4 py-2 font-medium text-fg-subtle">Why</th>
            </tr>
          </thead>
          <tbody>
            {visible.map((rule, index) => (
              <tr
                key={`${rule.rule_type}-${rule.seon_id}`}
                className="animate-row-in border-b border-line/60 last:border-b-0"
                style={{ animationDelay: `${Math.min(index, 20) * 8}ms` }}
              >
                <td className="px-4 py-2 align-top">
                  <span className="text-xs text-fg-subtle">{rule.seon_id}</span>
                  <span className="mt-0.5 block truncate text-fg" title={rule.name ?? ""}>
                    {formatText(rule.name)}
                  </span>
                </td>
                <td className="px-3 py-2 align-top">
                  <Badge tone={statusTone(rule.status)}>{rule.status}</Badge>
                </td>
                <td className="px-3 py-2 text-right align-top text-fg-muted">
                  {rule.rule_score}
                </td>
                <td
                  className={cn(
                    "px-3 py-2 text-right align-top",
                    rule.status === "matched" ? "text-decline" : "text-fg-subtle",
                  )}
                >
                  {rule.status === "matched" ? rule.score : EMPTY}
                </td>
                <td className="px-4 py-2 align-top text-xs leading-relaxed text-fg-muted">
                  {/*
                   * Пустую клетку читатель видит как «причина неизвестна». У
                   * `matched` и `not_matched` она пуста всегда (движок не пишет
                   * `reason`, когда условие просто сверили), и это ровно те два
                   * статуса, у которых причина известна лучше прочих. Подпись
                   * ставим только вместо пустоты и курсивом-приглушённо —
                   * рядом с настоящим `reason` её нельзя принять за разбор
                   * конкретного правила.
                   */}
                  {rule.reason ? (
                    rule.reason
                  ) : (
                    <span className="italic text-fg-subtle">
                      {STATUS_MEANING[rule.status] ?? EMPTY}
                    </span>
                  )}
                  {rule.missing_reasons.length > 0 ? (
                    <MissingReasons reasons={rule.missing_reasons} />
                  ) : (
                    rule.missing_fields.length > 0 && (
                      <span className="mt-0.5 block text-fg-subtle">
                        missing: {rule.missing_fields.join(", ")}
                      </span>
                    )
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <footer className="border-t border-line px-4 py-2 text-xs text-fg-subtle">
        {shown.length === 0
          ? "No rules with this status."
          : visible.length < shown.length
            ? `Showing the first ${formatInt(visible.length)} of ${formatInt(shown.length)} rules — filter by status to narrow the list.`
            : `${formatInt(shown.length)} rules.`}{" "}
        Open a rule from the{" "}
        <Link to="/scoring/custom" className="text-accent hover:underline">
          rules tabs
        </Link>{" "}
        to see its condition.
      </footer>
    </section>
  )
}
